import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Badge, Button, Flex, Group, Pagination, Stack, Title } from "@mantine/core";
import { useCallbackRef, useDisclosure } from "@mantine/hooks";

import { IconFilter } from "@tabler/icons-react";
import pluralize from "pluralize";

import { useMissingServicesCounts } from "@/billing/hooks/useMissingServicesCounts";
import { useMissingServicesTableData } from "@/billing/hooks/useMissingServicesTableData";
import { MISSING_SERVICES_PAGE_SIZE } from "@/billing/services/missing-services-table";
import {
  MissingServicesFilters,
  MissingServicesSortDirection,
} from "@/billing/types/missing-services";
import { TextWrapper } from "@/components/TextWrapper";
import { DevelopmentFeatureFlags, FeatureGate } from "@/feature-flags";
import usePagination from "@/utils/hooks/use-pagination";
import { PAGE_PARAM, updateSearchParams } from "@/utils/search-params";

import MissingServicesFilterDrawer from "./MissingServicesFilterDrawer";
import MissingServicesSummaryCards from "./MissingServicesSummaryCards";
import MissingServicesTable from "./MissingServicesTable";
import {
  buildMissingServicesFilterParams,
  countMissingServicesFilters,
  parseMissingServicesFilters,
} from "./utils/missing-services-filter-params";

const FILTER_ICON_SIZE = 14;

interface MissingServicesProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

function MissingServicesContent({ refreshKey = 0, onLoadingChange }: MissingServicesProps) {
  const { practiceId } = useParams<{ practiceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort");
  const { currentPage, changeCurrentPage, totalPages, changeTotalPages } = usePagination({
    pageSize: MISSING_SERVICES_PAGE_SIZE,
  });
  const [dismissRefreshKey, setDismissRefreshKey] = useState(0);
  const [isFilterDrawerOpen, { open: openFilterDrawer, close: closeFilterDrawer }] =
    useDisclosure(false);
  const committedFilters = useMemo(() => parseMissingServicesFilters(searchParams), [searchParams]);
  const filtersCount = countMissingServicesFilters(committedFilters);
  const { rows, isLoading, error, totalRows, dismissEncounter } = useMissingServicesTableData(
    practiceId,
    currentPage,
    refreshKey,
    committedFilters,
    resolveMissingServicesSortDirection(sortParam),
  );

  // `changeCurrentPage`'s identity changes on every page navigation (it closes over `currentPage`),
  // so listing it directly here would re-fire this effect right after a real page change and snap
  // back to page 1. `useCallbackRef` gives a stable reference that still calls the latest version.
  const changeCurrentPageCallback = useCallbackRef(changeCurrentPage);
  const isFirstSortRender = useRef(true);
  useEffect(() => {
    if (isFirstSortRender.current) {
      isFirstSortRender.current = false;
      return;
    }
    changeCurrentPageCallback(1);
  }, [sortParam, changeCurrentPageCallback]);

  // Write the filter and the page reset in a single navigation. Two separate `setSearchParams` calls
  // would lose the first one, because updaters called in the same tick both read the query string
  // from before the navigation (react-router #9757).
  const applyFilters = (filters: MissingServicesFilters) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        updateSearchParams(next, {
          ...buildMissingServicesFilterParams(filters),
          [PAGE_PARAM]: "1",
        });
        return next;
      },
      { preventScrollReset: true },
    );
  };
  // Both counters only ever go up, so their sum changes whenever either one does. A dismiss and an
  // outside refresh (the practice-level header button) both need to reload the summary cards.
  const { counts } = useMissingServicesCounts(practiceId, dismissRefreshKey + refreshKey);
  const onLoadingChangeCallback = useCallbackRef(onLoadingChange);

  useEffect(() => {
    changeTotalPages(totalRows);
  }, [changeTotalPages, totalRows]);

  // Dismissing the last row on the last page drops totalRows right away, in the same render as the
  // now-empty rows. totalPages only updates once the effect above runs, so it lags one render behind.
  // We derive staleness from totalRows directly instead of totalPages, so the empty-queue message
  // doesn't flash during that one render where rows and totalRows already agree but currentPage is
  // still stale.
  const freshTotalPages = totalRows > 0 ? Math.ceil(totalRows / MISSING_SERVICES_PAGE_SIZE) : 0;
  const isPageStale = freshTotalPages > 0 && currentPage > freshTotalPages;

  useEffect(() => {
    if (isPageStale) {
      changeCurrentPage(freshTotalPages);
    }
  }, [isPageStale, freshTotalPages, changeCurrentPage]);

  useEffect(() => {
    onLoadingChangeCallback?.(isLoading || isPageStale);
  }, [isLoading, isPageStale, onLoadingChangeCallback]);

  useEffect(() => {
    return () => onLoadingChangeCallback?.(false);
  }, [onLoadingChangeCallback]);

  return (
    <Stack gap="lg" data-testid="missing-services">
      <MissingServicesSummaryCards counts={counts} />
      <Stack gap="lg">
        <Group gap="xs" align="center" justify="space-between">
          <Title order={2}>Missing Services</Title>
          <Button
            variant="outline"
            onClick={openFilterDrawer}
            data-testid="missing-services-filter-button"
            leftSection={<IconFilter aria-hidden size={FILTER_ICON_SIZE} />}
            rightSection={
              filtersCount > 0 && (
                <Badge
                  size="sm"
                  color="red.7"
                  data-testid="missing-services-filter-count"
                  aria-label={`${filtersCount} ${pluralize("filter", filtersCount)} applied`}
                >
                  <TextWrapper fz="sm" variant="bold">
                    {filtersCount}
                  </TextWrapper>
                </Badge>
              )
            }
          >
            Filter
          </Button>
        </Group>
        <MissingServicesTable
          rows={rows}
          error={error}
          onRemove={dismissEncounter}
          isLoading={isLoading || isPageStale}
          onRefresh={() => setDismissRefreshKey((key) => key + 1)}
        />
        <Flex justify="center">
          <Pagination
            hideWithOnePage
            total={totalPages}
            value={currentPage}
            onChange={changeCurrentPage}
            data-testid="missing-services-pagination"
          />
        </Flex>
      </Stack>
      <MissingServicesFilterDrawer
        onApply={applyFilters}
        opened={isFilterDrawerOpen}
        onClose={closeFilterDrawer}
        committedFilters={committedFilters}
      />
    </Stack>
  );
}

export default function MissingServices(props: MissingServicesProps) {
  return (
    <FeatureGate name={DevelopmentFeatureFlags.PracticeBilling}>
      <MissingServicesContent {...props} />
    </FeatureGate>
  );
}

/** Reads Date of Encounter's sort direction off the `sort` URL param `SortableTh` writes. */
function resolveMissingServicesSortDirection(
  sortParam: string | null,
): MissingServicesSortDirection | undefined {
  if (sortParam === "date") return "asc";
  if (sortParam === "-date") return "desc";
  return undefined;
}
